import { useQuery, useMutation } from '@apollo/client'
import { ToastContext } from 'contexts'
import { useContext } from 'react'

import validatorPendingAccountsGql from 'gql/admin/validatorPendingAccounts.gql'
import activateValidatorAccountGql from 'gql/admin/activateValidatorAccount.gql'
import deleteAccountGql from 'gql/admin/deleteAccount.gql'
import { Account } from 'types/account'

interface UseValidatorPendingAccountsReturn {
  data: {
    items: Pick<Account, 'id' | 'name' | 'account_type' | 'active'>[]
    total_count: number
  }
  error: any
  loading: boolean
  refetch: ({ url }: { url: string }) => void
}

interface AccountActionReturn {
  message: string
  success: boolean
  account: Pick<Account, 'id' | 'name' | 'account_type'>
}

export interface UseActivateValidatorAccountService {
  activateValidatorAccount: (
    account_id: string,
  ) => Promise<{ activateValidatorAccount: AccountActionReturn }>
  loading: boolean
}

/**
 * Retrieves validator pending accounts based on the provided URL.
 *
 * @param {string} url - The URL used to retrieve the validator pending accounts.
 * @return {UseValidatorPendingAccountsReturn} An object containing the data, error, loading, and refetch functions related to the validator pending accounts.
 */
export const useValidatorPendingAccounts = (url: string): UseValidatorPendingAccountsReturn => {
  const { data, error, loading, refetch } = useQuery(validatorPendingAccountsGql, {
    skip: !url,
    // fetchPolicy: 'cache-and-network',
    variables: { url: url },
  })

  return {
    data: data?.validatorPendingAccounts || { items: [], total_count: 0 },
    error,
    loading,
    refetch,
  }
}

/**
 * Returns a service object that can be used to activate a validator account.
 *
 * @return {Object} An object with the following properties:
 *   - activateValidatorAccount: A function that takes an account ID as a parameter and returns a Promise that resolves to the activated account data.
 *   - loading: A boolean indicating whether the mutation is currently loading.
 *
 * @throws {Error} If there is an error activating the validator account, the error is thrown and propagated to the caller.
 */
export const useActivateValidatorAccountService = () => {
  const { setToast } = useContext(ToastContext)
  const [mutation, { loading }] = useMutation(activateValidatorAccountGql)

  /**
   * Asynchronously activates a validator account.
   *
   * @param {string} account_id - The ID of the account to activate.
   * @return {Promise<UseActivateValidatorAccountService>} A Promise that resolves to the activated account data.
   */
  const activateValidatorAccount = async (
    account_id: string,
  ): Promise<UseActivateValidatorAccountService> => {
    try {
      const {
        data: { activateValidatorAccount },
      } = await mutation({
        variables: {
          account_id,
          input: {},
        },
      })

      return activateValidatorAccount
    } catch (error) {
      setToast({
        message: (error as Error)?.message ?? 'Error activating Validator Account',
        type: 'negative',
        open: true,
      })
      throw error // Rethrow the error to propagate it to the caller
    }
  }

  return { activateValidatorAccount, loading }
}

/**
 * A hook for deleting a Account.
 *
 * @param {string} account_id - The ID of the Account to delete.
 * @return {Promise<any>} A promise that resolves to the deleted Account data.
 */
export const useDeleteAccountService = () => {
  const { setToast } = useContext(ToastContext)

  const [mutation, { loading }] = useMutation(deleteAccountGql, {
    refetchQueries: [
      {
        query: validatorPendingAccountsGql,
        variables: { url: 'limit=20&page=1&active=true' },
      },
    ],
  })

  const deleteAccount = async (account_id: string) => {
    try {
      const {
        data: { deleteAccount },
      } = await mutation({
        variables: {
          account_id,
        },
      })

      return deleteAccount
    } catch (error) {
      setToast({
        message: (error as Error)?.message ?? 'Error deleting account',
        type: 'negative',
        open: true,
      })
      throw error // Rethrow the error to propagate it to the caller
    }
  }

  return { deleteAccount, loading }
}
